import { type DraftCitation, hasRenderableText, SOURCES } from '@aira/shared';

interface CitationListProps {
  citations: DraftCitation[];
  /** Renders a "+ Add Knowledge" footer button when provided (e.g. deep-links into the KB admin). */
  onAddKnowledge?: () => void;
}

export function CitationList({ citations, onAddKnowledge }: CitationListProps) {
  // Defensively drop entries with no source label AND no text — the backend
  // adapter already filters these, but the UI guards too so an upstream
  // regression can't ever render blank rows under "Sources · N".
  // Low-confidence RAG hits never get a detailed row — they're summarized by
  // the note below instead, so a weak match never gets cited as if it were
  // an authoritative source.
  const renderable = citations.filter(
    (c) => c.kind !== 'knowledge_low_confidence' && hasRenderableText(c),
  );
  const hasLowConfidenceKnowledge = citations.some((c) => c.kind === 'knowledge_low_confidence');
  if (renderable.length === 0 && !hasLowConfidenceKnowledge && !onAddKnowledge) return null;

  const addKnowledgeButton = onAddKnowledge && (
    <button
      type="button"
      onClick={onAddKnowledge}
      className="mx-3 mb-3 self-start rounded-lg bg-sf-primary px-3 py-1.5 text-xs font-semibold text-sf-secondary hover:bg-sf-primary-hover transition-colors cursor-pointer"
    >
      + Add Knowledge
    </button>
  );

  const lowConfidenceNote = (withDivider: boolean) =>
    hasLowConfidenceKnowledge && (
      <p
        className={`px-3 py-2 text-xs italic text-sf-gray ${withDivider ? 'border-t border-sf-primary/10' : 'pb-3 pt-0'}`}
      >
        Background knowledge consulted (low confidence — no confident match found).
      </p>
    );

  if (renderable.length === 0) {
    return (
      <div className="flex flex-col rounded-lg border border-sf-primary/10 bg-sf-primary-light">
        <p className="px-3 pt-3 pb-2 text-xs text-sf-gray">
          {hasLowConfidenceKnowledge
            ? 'No confident sources found for this reply.'
            : 'No sources found for this reply.'}
        </p>
        {lowConfidenceNote(false)}
        {addKnowledgeButton}
      </div>
    );
  }

  return (
    <details open className="rounded-lg border border-sf-primary/10 bg-sf-primary-light">
      <summary className="cursor-pointer select-none px-3 py-2 text-xs font-medium text-sf-primary hover:bg-sf-primary/5 list-none flex items-center gap-1">
        <span>Sources · {renderable.length}</span>
        <span className="ml-auto text-sf-gray">▼</span>
      </summary>
      <ul className="divide-y divide-sf-primary/10 border-t border-sf-primary/10">
        {renderable.map((c) => (
          <li key={c.id} className="px-3 py-2">
            {c.source && <p className="text-xs font-bold text-sf-dark">{c.source}</p>}
            <p className={`text-xs text-sf-gray line-clamp-2 ${c.source ? 'mt-0.5' : ''}`}>
              {c.text || '—'}
            </p>
            {c.kind !== 'live' && c.edit_url && (
              <a
                href={c.edit_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1 inline-block text-xs font-medium text-sf-primary hover:underline"
              >
                {SOURCES.updateKnowledge} ↗
              </a>
            )}
          </li>
        ))}
      </ul>
      {lowConfidenceNote(true)}
      {addKnowledgeButton && (
        <div className="flex flex-col border-t border-sf-primary/10 pt-3">{addKnowledgeButton}</div>
      )}
    </details>
  );
}
